if (!customElements.get("ilf-modal")) {
    class IlfModal extends HTMLElement {
        constructor() {
            super()
            this.isOpen = false
        }

        connectedCallback() {
            // Move modal to body for better z-index and positioning
            if (document.body && this.parentElement !== document.body) {
                document.body.appendChild(this)
            }

            const modal = this.querySelector(".ilf-modal-content-wrapper")
            const close = this.querySelector(".ilf-modal-close")
            const modalId = this.id

            const btn = document.querySelector(`[data-ilf-modal-open="${modalId}"]`)

            if (btn && modal && close) {
                // Store references for cleanup
                this.btn = btn
                this.close = close
                this.modal = modal

                btn.addEventListener("click", this.openModal.bind(this))
                close.addEventListener("click", this.closeModal.bind(this))
                this.addEventListener("click", this.handleBackdropClick.bind(this))

                // Add keyboard support
                this.addEventListener("keydown", this.handleKeydown.bind(this))
            }
        }

        openModal() {
            this.style.display = "flex"
            this.isOpen = true
            // Prevent body scroll when modal is open
            document.body.style.overflow = "hidden"
            this.close.focus()
        }

        closeModal() {
            this.style.display = "none"
            this.isOpen = false
            // Restore body scroll when modal is closed
            document.body.style.overflow = ""
            if (this.btn) {
                this.btn.focus()
            }
        }

        handleBackdropClick(event) {
            if (event.target === this) {
                this.closeModal()
            }
        }

        handleKeydown(event) {
            if (event.key === "Escape" && this.isOpen) {
                this.closeModal()
            }
        }

        disconnectedCallback() {
            // Clean up body scroll when modal is removed
            if (this.isOpen) {
                document.body.style.overflow = ""
            }
        }
    }

    customElements.define("ilf-modal", IlfModal)
}

/**
 * ILF UPSELL COMPONENT
 * Handles add-to-cart logic, inventory validation, and cart drawer updates.
 */
if (!customElements.get("ilf-upsell")) {
  class IlfUpsell extends HTMLElement {
    constructor() {
      super();
      
      // 1. Initialize State from Data Attributes
      this.variantId = this.dataset.variantId;
      this.maxStock = parseInt(this.dataset.maxStock) || 9999;
      this.isAvailable = this.dataset.available === "true";
      this.basePrice = parseInt(this.dataset.price) || 0;
      
      // 2. Select Internal Elements
      this.submitBtn = this.querySelector(".ilf-upsell-submit-btn");
      this.spinner = this.querySelector(".loading__spinner");
      this.buttonText = this.querySelector(".button-text");
      this.messageContainer = this.querySelector(".ilf-btn-message");
      
      this.qtyInput = this.querySelector(".ilf-qty-input");
      this.plusBtn = this.querySelector('[data-action="plus"]');
      this.minusBtn = this.querySelector('[data-action="minus"]');
      this.errorContainer = this.querySelector(".ilf-error-message");
      this.priceElement = this.querySelector(".price-value"); // Price display element
    }

    connectedCallback() {
      // If sold out or missing button, do not initialize listeners
      if (!this.isAvailable || !this.submitBtn) return;
      
      this.initEventListeners();
      this.updatePriceDisplay();
    }

    initEventListeners() {
      // Add to Cart Action
      this.submitBtn.addEventListener("click", () => {
        this.handleAddToCart();
      });

      // Quantity Logic (Only if the input exists in the DOM)
      if (this.qtyInput) {
        if (this.plusBtn) {
          this.plusBtn.addEventListener("click", () => this.updateQuantity(1));
        }
        if (this.minusBtn) {
          this.minusBtn.addEventListener("click", () => this.updateQuantity(-1));
        }
        
        // Manual typing validation
        this.qtyInput.addEventListener("change", () => this.validateQuantity());
        this.qtyInput.addEventListener("input", () => this.validateQuantity());
      }
    }

    // ==========================================
    // LOGIC: Quantity Management
    // ==========================================

    updateQuantity(change) {
      if (!this.qtyInput) return;
      
      let currentQty = parseInt(this.qtyInput.value) || 0;
      let newQty = currentQty + change;
      
      this.qtyInput.value = newQty;
      this.validateQuantity();
    }

    updatePriceDisplay() {
      if (!this.priceElement) return;
      
      const quantity = parseInt(this.qtyInput.value) || 1;
      const totalCents = this.basePrice * quantity;
      
      // Simple formatting: cents -> decimal with 2 places
      const total = (totalCents / 100).toFixed(2);
      this.priceElement.textContent = total;
    }

    validateQuantity() {
      if (!this.qtyInput) return;

      let value = parseInt(this.qtyInput.value);
      const min = 1;

      const effectiveValue = isNaN(value) ? min : value;

      // 1. Enforce Minimum
      if (effectiveValue < min) {
        this.qtyInput.value = min;
        value = min; // update local var for max check
      }

      // 2. Enforce Maximum (Stock Check)
      // Use effectiveValue to ensure we don't block empty inputs unnecessarily
      if (effectiveValue > this.maxStock) {
        // Gray out button & Show Message
        this.submitBtn.classList.add("unavailable", "has-message");
        this.submitBtn.setAttribute("disabled", "disabled");
        
        if (this.messageContainer) {
            this.messageContainer.textContent = `Max Available: ${this.maxStock}`;
            this.messageContainer.classList.remove('hidden');
        }
      } else {
        // Restore button & Hide Message
        this.submitBtn.classList.remove("unavailable", "has-message");
        this.submitBtn.removeAttribute("disabled");
        
        if (this.messageContainer) {
            this.messageContainer.classList.add('hidden');
            this.messageContainer.textContent = '';
        }
        
        this.updatePriceDisplay();
      }
    }

    // ==========================================
    // LOGIC: Add To Cart (AJAX)
    // ==========================================

    toggleLoading(isLoading) {
        if (isLoading) {
            this.submitBtn.classList.add('is-loading');
        } else {
            this.submitBtn.classList.remove('is-loading');
            this.validateQuantity();
        }
    }

    handleAddToCart() {
      this.hideErrorMessage();
      
      // Default to 1 if quantity input is missing or invalid/empty
      let quantity = 1;
      if (this.qtyInput) {
        const parsed = parseInt(this.qtyInput.value);
        if (!isNaN(parsed) && parsed > 0) {
            quantity = parsed;
        }
      }

      const formData = {
        items: [{
          id: this.variantId,
          quantity: quantity
        }],
        sections: "cart-drawer,cart-icon-bubble"
      };

      this.toggleLoading(true);

      this.addProductToCart(formData);
    }

    addProductToCart(formData) {
      fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      })
      .then(response => {
        return response.json();
      })
      .then(data => {
        this.toggleLoading(false);

        // Handle Errors (e.g. 422 Unprocessable Entity - Sold Out)
        if (data.status === 422) {
          this.showErrorMessage(data.description);
          return;
        }

        // Success: Update UI Sections
        if (data.sections) {
          this.updateCartSections(data.sections);
        }
        
        // Optional: Open Cart Drawer if method exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.open) cartDrawer.open();
      })
      .catch(error => {
        console.error("Error:", error);
        this.toggleLoading(false);
        this.showErrorMessage("Error adding to cart");
      });
    }

    // ==========================================
    // LOGIC: DOM Parsing & Updates
    // ==========================================

    // Helper: Extract inner HTML of a specific section
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
    }

    updateCartSections(sections) {
      // 1. Update Cart Drawer
      const cartDrawerHTML = sections["cart-drawer"];
      if (cartDrawerHTML) {
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) {
          // Dawn typically wraps content in class .cart-drawer
          const newCartDrawerContent = this.getSectionInnerHTML(cartDrawerHTML, ".cart-drawer") || this.getSectionInnerHTML(cartDrawerHTML, "cart-drawer");
          
          if (newCartDrawerContent) {
            cartDrawer.classList.remove("is-empty");
            
            // Depending on theme version, replace the inner container
            const drawerInner = document.getElementById("CartDrawer");
            if (drawerInner) {
              drawerInner.innerHTML = this.getSectionInnerHTML(cartDrawerHTML, "#CartDrawer");
            } else {
               // Fallback if ID not found
               cartDrawer.innerHTML = newCartDrawerContent;
            }
          }
        }
      }

      // 2. Update Cart Icon
      const cartIconHTML = sections["cart-icon-bubble"];
      if (cartIconHTML) {
        const newCartIconContent = this.getSectionInnerHTML(cartIconHTML, "#shopify-section-cart-icon-bubble");
        const iconContainer = document.querySelector("#cart-icon-bubble");
        if (iconContainer && newCartIconContent) {
          iconContainer.innerHTML = newCartIconContent;
        }
      }
    }

    // ==========================================
    // LOGIC: Error Handling
    // ==========================================

    showErrorMessage(msg) {
      if (this.errorContainer) {
        this.errorContainer.innerText = msg;
        this.errorContainer.classList.remove("hidden");
      } else {
        alert(msg); // Fallback if container missing
      }
    }

    hideErrorMessage() {
      if (this.errorContainer) {
        this.errorContainer.classList.add("hidden");
      }
    }
  }

  // Register Component
  customElements.define("ilf-upsell", IlfUpsell);
}

/**
 * IlfSlider
 * A generic wrapper for Swiper.js
 * Expects Swiper HTML structure (swiper, swiper-wrapper, swiper-slide) as children.
 *
 * Usage:
 * <ilf-slider data-loop="true" data-autoplay="3000" data-effect="slide">
 *   ... swiper html ...
 * </ilf-slider>
 */

if (!customElements.get("ilf-slider")) {
  class IlfSlider extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
    }

    connectedCallback() {
      // Small timeout ensures DOM children are fully parsed by the browser
      requestAnimationFrame(() => this.initSwiper());
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy();
        this.swiperInstance = null;
      }
    }

    /**
     * Parse data attributes to build Swiper config
     */
    getOptions() {
      const loop = this.dataset.loop === "true";
      const autoplayDelay = parseInt(this.dataset.autoplay, 10);
      const effect = this.dataset.effect || "slide"; // slide, fade, cube, etc.
      const slidesPerView = this.dataset.slidesPerView ? parseFloat(this.dataset.slidesPerView) : 1;
      const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 0;

      // Basic Config
      const config = {
        loop: loop,
        effect: effect,
        slidesPerView: slidesPerView,
        spaceBetween: spaceBetween,
        grabCursor: true,
        threshold: 10, // Touch sensitivity
        navigation: {},
        pagination: {},
      };

      // Handle Autoplay
      if (!isNaN(autoplayDelay) && autoplayDelay > 0) {
        config.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
        };
      }

      // Handle Navigation (Scoped to this element)
      const nextEl = this.querySelector(".swiper-button-next");
      const prevEl = this.querySelector(".swiper-button-prev");

      if (nextEl && prevEl) {
        config.navigation = {
          nextEl: nextEl,
          prevEl: prevEl,
        };
      }

      // Handle Pagination (Optional, if markup exists)
      const paginationEl = this.querySelector(".swiper-pagination");
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }

      return config;
    }

    initSwiper() {
      // Prevent double init
      if (this.swiperInstance) return;

      const swiperContainer = this.querySelector(".swiper");
      
      if (!swiperContainer) {
        console.warn("IlfSlider: No element with class .swiper found inside <ilf-slider>");
        return;
      }

      if (typeof Swiper === "undefined") {
        console.error("IlfSlider: Swiper.js is not loaded globally.");
        return;
      }

      const config = this.getOptions();
      this.swiperInstance = new Swiper(swiperContainer, config);
    }
  }

  customElements.define("ilf-slider", IlfSlider);
}